import { useEffect, useRef } from 'react';
import { useActiveContextOptional } from '@/auth/ActiveContextProvider';
import { bumpAccessEpoch } from './access-epoch';
import { apiClient } from './api-client';

/** Every minute, and each time the console comes back to the foreground (decision 151). */
export const ACCESS_VERSION_POLL_MS = 60 * 1000;

/**
 * Notices a change of the caller's access made elsewhere — by another administrator, in another
 * tab, or a module switched on — and bumps the access epoch, so the menu, the organizations and the
 * permissions are read again without a reload.
 *
 * `GET /v1/access/version` answers an opaque fingerprint for the active organization. The first
 * answer only sets the baseline; any later different one bumps the epoch. An error or a 429 keeps the
 * last value and the next tick tries again. A switch of organization starts a new baseline, since the
 * reads already run again on a switch.
 */
export function useAccessVersionPoller(): void {
  const context = useActiveContextOptional();
  const tenantId = context?.active?.tenantId;
  const organizationId = context?.active?.organizationId;
  const last = useRef<string | null>(null);

  useEffect(() => {
    last.current = null;
    if (tenantId === undefined || organizationId === undefined) {
      return;
    }
    let cancelled = false;

    function check() {
      if (tenantId === undefined || organizationId === undefined) return;
      apiClient
        .GET('/v1/access/version', {
          params: {
            header: { 'X-Active-Tenant-Id': tenantId, 'X-Active-Organization-Id': organizationId },
          },
        })
        .then(({ data, response }) => {
          if (cancelled || !response.ok || data === undefined) return;
          const previous = last.current;
          last.current = data.accessVersion;
          if (previous !== null && previous !== data.accessVersion) {
            bumpAccessEpoch();
          }
        })
        .catch(() => {});
    }

    function onVisible() {
      if (document.visibilityState === 'visible') check();
    }

    check();
    const timer = window.setInterval(check, ACCESS_VERSION_POLL_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [tenantId, organizationId]);
}
